import { apiRequest } from '@/utils/api-request';
import { clearNodeResults } from '@/pages/workflow/helper/clear-node-results';
import { Workflow } from '@/types/workflow-types';
import { v4 as uuidv4 } from 'uuid';
import { NodeType } from '@/types/node-types';

export interface UpdateWorkflowResponse {
  data?: {
    created_at: string;
    message: string;
    orgid: string;
    success: boolean;
    updated_at: string;
    workflow_id: string;
  };
  success?: boolean;
  message?: string;
  detail?: string;
}

/**
 * Transforms frontend nodes and edges to the backend WorkflowPayload schema and persists via v1 API.
 * @param workflow Workflow
 * @param orgId Organization ID
 * @returns Backend response JSON
 */
export async function updateWorkflow(workflow: Workflow, orgId: string) {
  const {
    id,
    name,
    nodes,
    edges,
    publish_to_template,
    publish_to_mcp,
    publish_to_api,
    software_repo_url,
    software_repo_branch,
    software_repo_mode,
    description,
    screenshot,
    icon,
    color,
    bg_color,
    metadata,
  } = workflow;
  // Generate a new session id
  const sessionId = uuidv4();

  // Filter out SelectNode type nodes before sending to backend
  const selectNodeIds = new Set(
    nodes
      .filter((node) => node.type === NodeType.SelectNode)
      .map((node) => node.id),
  );
  const nodesWithoutSelectNodes = nodes.filter(
    (node) => node.type !== NodeType.SelectNode,
  );
  const filteredNodes = clearNodeResults(nodesWithoutSelectNodes);

  // Filter out edges connected to SelectNode nodes
  const filteredEdges = edges.filter((edge) => !selectNodeIds.has(edge.target));

  // Convert payload to the format expected by apiRequest
  const payload = {
    id,
    session_id: sessionId,
    publish_to_template,
    publish_to_mcp,
    publish_to_api,
    software_repo_url,
    software_repo_branch,
    software_repo_mode,
    name,
    nodes: filteredNodes,
    edges: filteredEdges,
    description,
    screenshot,
    icon,
    color,
    bg_color,
    metadata,
  };

  const response = await apiRequest<UpdateWorkflowResponse>({
    url: `/api/v1/orgs/${orgId}/workflows`,
    method: 'POST',
    target: 'workflow',
    body: payload,
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to update workflow',
    };
  }

  return response;
}
